import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import './App.css';


/* ─────────────────────────────────────────────
   TRANSLATIONS
───────────────────────────────────────────── */
const translations = {
  zh: {
    nav: {
      visualAids: '视觉辅助',
      howItWorks: '使用方法',
      support: '专家支持',
      community: '用户社区',
      shopNow: '立即购买',
    },
    hero: {
      badge: '解放双手 · 独立视界',
      titleLine1: '重新看清世界，',
      titleAccent: '清晰全新体验',
      desc: '精准工程与优雅设计完美结合。体验 LumaSeeker P2 带来的独立视界。',
      btnBuy: '立即订购 P2 型号',
      btnVideo: '观看演示',
    },
    bento: {
      badge: '精心设计',
      title: '专为您的生活而设计',
      desc: '每个细节都旨在消除障碍，让世界重新清晰地呈现在眼前。',
      items: [
        {
          icon: 'zoom_in',
          title: '5倍水晶级放大',
          desc: '光学级镜片提供无畸变的全域清晰度，让最小的文字都清晰可读。',
        },
        {
          icon: 'construction',
          title: '耐用合金支架',
          desc: '航空级铝合金，轻盈坚固，每次使用都稳定可靠。',
        },
        {
          icon: 'sync_alt',
          title: '360°无缝调节',
          desc: '流畅关节随意调节，找到最舒适的阅读姿势。',
        },
        {
          icon: 'light_mode',
          title: '夜视照明',
          desc: '柔光 LED 减少夜间眼疲劳，模拟自然日光。',
        },
      ],
    },
    why: {
      title: '为何选择 LumaSeeker？',
      stats: [
        { value: '15,000+', label: '用户受益' },
        { value: '评选第一', label: '清晰度认证' },
        { value: '90天', label: '保障承诺' },
        { value: '北美', label: '本地支持' },
      ],
      items: [
        {
          icon: 'check',
          title: '无与伦比的清晰度',
          desc: '在老年人阅读清晰度测试中荣获边缘到边缘清晰度第一名。',
        },
        {
          icon: 'support_agent',
          title: '终身支持',
          desc: '北美本地团队，为您的社区提供专业支持专员。',
        },
        {
          icon: 'verified',
          title: '90天舒适保障',
          desc: '无风险居家试用。如果它没有改变您的视界，我们全额退款。',
        },
      ],
    },
    testimonials: {
      title: '独立视界之声',
      desc: '每晚信赖 LumaSeeker 的真实用户故事。',
      items: [
        {
          initials: 'ME',
          name: '马小姐',
          meta: '满意用户 · 2023年起',
          text: '我终于可以不用眯眼看报纸和药瓶了。就像拥有了年轻的眼睛。',
          featured: false,
        },
        {
          initials: 'RJ',
          name: '罗先生',
          meta: '工程师（退休）',
          text: '做工质量非常出色，放在哪里就待在哪里。这不仅仅是一个放大镜，它是一件家具。',
          featured: true,
        },
        {
          initials: 'SL',
          name: '苏女士',
          meta: '美国佛罗里达州',
          text: '客服真的太棒了，全程指导我设置。真正的高端体验。',
          featured: false,
        },
      ],
    },
    setup: {
      title: '设置超简单',
      desc: '我们不让您猜测。大字体用户手册专为清晰设计，包含逐步图示和易于遵循的指南，让您从开箱那一刻起就充满信心。',
      features: [
        { icon: 'description', text: '24pt 字体高对比度印刷' },
        { icon: 'videocam', text: '在线视频教程随时可看' },
      ],
    },
    contact: {
      title: '联系我们',
      desc: '如果您对产品有任何问题，请随时与我们联系。',
      email: '邮箱',
      emailReply: '24小时内回复',
      hours: '服务时间',
      weekdays: '周一至周五',
      form: {
        title: '发送消息',
        name: '您的姓名',
        namePlaceholder: '请输入姓名',
        emailLabel: '邮箱地址',
        emailPlaceholder: '请输入邮箱',
        message: '您的留言',
        messagePlaceholder: '请输入留言内容...',
        submit: '发送消息',
      },
    },
    cta: {
      title: '准备好重新发现您的世界了吗？',
      desc: '加入数千名已重获独立与清晰视界的老年人行列。',
      btnBuy: '立即订购 P2 型号',
      btnContact: '咨询专家',
      badges: ['免费追踪配送', '90天试用期', '终身保修'],
      badgeIcons: ['local_shipping', 'shield_moon', 'verified'],
    },
    presale: {
      title: '🎉 预售进行中',
      body: '该商品目前正在预售阶段，如需了解详情或预订，请联系我们：',
      email: 'Lumaseeker@gmail.com',
      close: '关闭',
    },
    footer: {
      desc: '赋能视力，迎接更光明的明天。我们相信清晰是独立的基础。',
      cols: [
        {
          heading: '实用链接',
          links: ['联系我们', '配送与退货', '无障碍声明'],
        },
        {
          heading: '公司',
          links: ['关于我们', '隐私政策', '服务条款'],
        },
        {
          heading: '支持',
          links: ['支持中心', '退换政策', '联系我们'],
        },
      ],
      stayInformed: '保持关注',
      emailPlaceholder: '邮箱地址',
      joinBtn: '订阅',
      rights: '© 2026 LumaSeeker. 赋能视力，迎接更光明的明天。',
      a11y: '无障碍设计',
      typeface: 'Lexend 字体',
    },
  },

  en: {
    nav: {
      visualAids: 'Visual Aids',
      howItWorks: 'How It Works',
      support: 'Expert Support',
      community: 'Community',
      shopNow: 'SHOP NOW',
    },
    hero: {
      badge: 'Independence Restored',
      titleLine1: 'See Clearly Again —',
      titleAccent: 'Magnifying Glass for Seniors',
      desc: 'Precision engineering meets effortless design. Experience Independence Restored with the Ultimate LumaSeeker P2.',
      btnBuy: 'Order Your P2 Model Today',
      btnVideo: 'Watch It Work',
    },
    bento: {
      badge: 'Precision Crafted',
      title: 'Engineered for Your Lifestyle',
      desc: 'Every detail is designed to remove barriers and bring the world back into sharp, beautiful focus.',
      items: [
        {
          icon: 'zoom_in',
          title: '5X Crystal Magnification',
          desc: 'Our signature optical-grade lens provides edge-to-edge clarity without distortion, making even the smallest text easy to navigate.',
        },
        {
          icon: 'construction',
          title: 'Durable Alloy Bracket',
          desc: 'Lightweight yet resilient aerospace-grade aluminum ensures stability during every use.',
        },
        {
          icon: 'sync_alt',
          title: '360° Seamless Adjustment',
          desc: 'Fluid motion joints allow you to position the lens at any angle, catering to your perfect reading posture.',
        },
        {
          icon: 'light_mode',
          title: 'Night-Sight Illumination',
          desc: 'Integrated soft-glow LEDs reduce eye strain during evening hours, mimicking natural daylight.',
        },
      ],
    },
    why: {
      title: 'Why LumaSeeker?',
      stats: [
        { value: '15,000+', label: 'Lives Restored' },
        { value: 'Voted #1', label: 'For Sharpness' },
        { value: '90-Day', label: 'Guarantee' },
        { value: 'NA', label: 'Based Support' },
      ],
      items: [
        {
          icon: 'check',
          title: 'Unmatched Clarity',
          desc: 'Voted #1 for edge-to-edge sharpness in senior readability trials.',
        },
        {
          icon: 'support_agent',
          title: 'Lifelong Support',
          desc: 'Based in North America with dedicated support specialists for our community.',
        },
        {
          icon: 'verified',
          title: '90-Day Comfort Guarantee',
          desc: "Try it at home risk-free. If it doesn't transform your vision, we'll take it back.",
        },
      ],
    },
    testimonials: {
      title: 'Voices of Independence',
      desc: 'Real stories from the people who trust LumaSeeker every night.',
      items: [
        {
          initials: 'ME',
          name: 'Martha E.',
          meta: 'Satisfied User since 2023',
          text: "I can finally read the newspaper and my medicine bottles without squinting. It's like having young eyes again.",
          featured: false,
        },
        {
          initials: 'RJ',
          name: 'Robert J.',
          meta: 'Engineer (Ret.)',
          text: "The build quality is exceptional. It stays exactly where I put it. This isn't just a magnifier, it's a piece of furniture.",
          featured: true,
        },
        {
          initials: 'SL',
          name: 'Susan L.',
          meta: 'Florida, USA',
          text: 'The customer service was incredible. They walked me through everything. A truly premium experience.',
          featured: false,
        },
      ],
    },
    setup: {
      title: 'Setup Made Simple',
      desc: "We don't leave you guessing. Our Large-Print User Manual is designed for absolute clarity, with step-by-step illustrations and easy-to-follow guides that build your confidence from the moment you unbox.",
      features: [
        { icon: 'description', text: '24pt Font High-Contrast Printing' },
        { icon: 'videocam', text: 'Online Video Tutorials Included' },
      ],
    },
    contact: {
      title: 'Contact Us',
      desc: 'If you have any questions, please contact us.',
      email: 'Email',
      emailReply: 'Response within 24 hours',
      hours: 'Support Hours',
      weekdays: 'Monday – Friday',
      form: {
        title: 'Send a Message',
        name: 'Your Name',
        namePlaceholder: 'Enter your name',
        emailLabel: 'Email Address',
        emailPlaceholder: 'Enter your email',
        message: 'Your Message',
        messagePlaceholder: 'Enter your message…',
        submit: 'Send Message',
      },
    },
    cta: {
      title: 'Ready to rediscover your world?',
      desc: 'Join thousands of seniors who have reclaimed their independence and clarity today.',
      btnBuy: 'Order Your P2 Model Today',
      btnContact: 'Speak to a Specialist',
      badges: ['Free Tracked Shipping', '90-Day Trial Period', 'Lifetime Warranty'],
      badgeIcons: ['local_shipping', 'shield_moon', 'verified'],
    },
    presale: {
      title: '🎉 Now Pre-Selling',
      body: 'This product is currently in pre-sale. For details or to place an order, please contact us at:',
      email: 'Lumaseeker@gmail.com',
      close: 'Close',
    },
    footer: {
      desc: 'Empowering vision for a brighter tomorrow. We believe that clarity is the foundation of independence.',
      cols: [
        {
          heading: 'Helpful Links',
          links: ['Contact Us', 'Shipping & Returns', 'Accessibility Statement'],
        },
        {
          heading: 'Company',
          links: ['Our Story', 'Privacy Policy', 'Terms of Service'],
        },
        {
          heading: 'Support',
          links: ['Support Center', 'Return Policy', 'Contact Us'],
        },
      ],
      stayInformed: 'Stay Informed',
      emailPlaceholder: 'Email Address',
      joinBtn: 'Join',
      rights: '© 2026 LumaSeeker. Empowering vision for a brighter tomorrow.',
      a11y: 'Designed for Accessibility',
      typeface: 'Lexend Typeface',
    },
  },
};

/* ─────────────────────────────────────────────
   APP
───────────────────────────────────────────── */
function App() {
  const [scrollY, setScrollY]       = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isPresaleOpen, setIsPresaleOpen] = useState(false);
  const [activeNav, setActiveNav]   = useState('visual-aids');
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [slideDir, setSlideDir] = useState(null);
  const trackRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const videoRef = useRef(null);
  const t = translations.en;

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    if (trackRef.current) trackRef.current.style.transform = 'translateX(-20%)';
  }, []);

  const slide = useCallback((direction) => {
    if (isAnimatingRef.current || !trackRef.current) return;
    isAnimatingRef.current = true;
    setSlideDir(direction);
    const track = trackRef.current;
    const n = translations.en.testimonials.items.length;
    const targetX = direction === 'next' ? -40 : 0;
    track.style.transition = 'transform 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    setTimeout(() => { track.style.transform = `translateX(${targetX}%)`; }, 16);
    const finish = () => {
      clearTimeout(fallback);
      track.removeEventListener('transitionend', onEnd);
      track.style.transition = 'none';
      track.style.transform = 'translateX(-20%)';
      setSlideDir(null);
      setTestimonialIndex(prev => direction === 'next' ? (prev + 1) % n : (prev - 1 + n) % n);
      isAnimatingRef.current = false;
    };
    const onEnd = (e) => { if (e.propertyName === 'transform') finish(); };
    const fallback = setTimeout(finish, 700);
    track.addEventListener('transitionend', onEnd, { once: true });
  }, []);

  const navLinks = [
    { key: 'visual-aids', label: t.nav.visualAids, href: '#features' },
    { key: 'how-it-works', label: t.nav.howItWorks, href: '#setup' },
    { key: 'support', label: t.nav.support, href: '#contact' },
    { key: 'community', label: t.nav.community, href: '#testimonials' },
  ];

  /* ── CONTACT FORM SUBMIT ── */
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const btn  = form.querySelector('button[type="submit"]');
    const origText = btn.textContent;
    const formData = {
      name:    form.from_name.value,
      email:   form.from_email.value,
      message: form.message.value,
    };
    btn.disabled = true;
    btn.textContent = 'Sending…';
    btn.style.opacity = '0.6';
    try {
      const res  = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      alert('✅ Message sent! We will respond within 24 hours.');
      form.reset();
    } catch (err) {
      console.error(err);
      alert('❌ Failed to send. Please email us at info@lumaseeker.com');
    } finally {
      btn.disabled = false;
      btn.textContent = origText;
      btn.style.opacity = '1';
    }
  };

  /* ── CLOSE VIDEO ── */
  const closeVideo = () => {
    if (videoRef.current) videoRef.current.pause();
    setIsVideoOpen(false);
  };

  return (
    <div className="min-h-screen bg-surface text-on-surface" style={{ fontFamily: "'Lexend', sans-serif" }}>

      {/* ══════════════════════════════════════
          NAV
      ══════════════════════════════════════ */}
      <header className={`w-full top-0 sticky z-50 glass-nav transition-shadow duration-300 ${scrollY > 20 ? 'atmospheric-shadow' : ''}`}>
        <nav className="flex justify-between items-center px-6 md:px-12 py-5 max-w-screen-2xl mx-auto">

          {/* Logo */}
          <div className="flex items-center gap-4">
            <img src="/favicon-32x32.png" alt="LumaSeeker" className="h-10 md:h-12 w-auto" />
            <span className="text-xl font-bold tracking-wider text-primary hidden lg:block uppercase">
              LumaSeeker
            </span>
          </div>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8 text-lg font-medium">
            {navLinks.map(({ key, label, href }) => (
              <a
                key={key}
                href={href}
                onClick={() => setActiveNav(key)}
                className={`transition-colors duration-200 ${
                  activeNav === key
                    ? 'text-primary font-bold border-b-4 border-primary-container pb-1'
                    : 'text-primary-container hover:text-secondary'
                }`}
              >
                {label}
              </a>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-4">
            <span className="material-symbols-outlined text-primary-container hidden md:block cursor-pointer hover:text-primary transition-colors">
              account_circle
            </span>
            <button
              className="btn-primary px-5 py-2 font-bold tracking-tight text-sm md:text-base hidden sm:block"
              onClick={() => setIsPresaleOpen(true)}
            >
              {t.nav.shopNow}
            </button>
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-primary"
              onClick={() => setIsMenuOpen(o => !o)}
              aria-label="Toggle menu"
            >
              <span className="material-symbols-outlined">
                {isMenuOpen ? 'close' : 'menu'}
              </span>
            </button>
          </div>
        </nav>

        {/* Nav bottom line */}
        <div className="bg-surface-container-low h-[2px] w-full" />

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-surface border-t border-outline-variant/10 px-6 py-6 space-y-5">
            {navLinks.map(({ key, label, href }) => (
              <a
                key={key}
                href={href}
                className="block text-lg font-medium text-primary hover:text-secondary transition-colors"
                onClick={() => { setActiveNav(key); setIsMenuOpen(false); }}
              >
                {label}
              </a>
            ))}
            <button
              className="btn-primary w-full py-3 font-bold text-base mt-2"
              onClick={() => { setIsPresaleOpen(true); setIsMenuOpen(false); }}
            >
              {t.nav.shopNow}
            </button>
          </div>
        )}
      </header>

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section className="relative px-6 md:px-12 pt-10 pb-16 md:pt-14 md:pb-24 max-w-screen-2xl mx-auto overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">

          {/* Left — text */}
          <div className="md:col-span-6 z-10">
            <span className="text-secondary font-bold tracking-widest uppercase block mb-4" style={{ fontSize: '0.85rem' }}>
              {t.hero.badge}
            </span>
            <h1
              className="font-extrabold text-primary tracking-tighter mb-8"
              style={{ fontSize: 'clamp(2.5rem, 4.2vw, 4.8rem)', lineHeight: 1.08 }}
            >
              {t.hero.titleLine1}
              <br />
              <span className="text-secondary">{t.hero.titleAccent}</span>.
            </h1>
            <p
              className="text-on-surface-variant leading-relaxed max-w-xl mb-12"
              style={{ fontSize: 'clamp(1.1rem, 1.5vw, 1.4rem)' }}
            >
              {t.hero.desc}
            </p>
            <div className="flex flex-col sm:flex-row gap-6">
              <button
                className="btn-primary font-bold atmospheric-shadow"
                style={{ fontSize: '1.1rem', padding: '1.25rem 2.5rem' }}
                onClick={() => setIsPresaleOpen(true)}
              >
                {t.hero.btnBuy}
              </button>
              <button
                onClick={() => setIsVideoOpen(true)}
                className="btn-ghost font-bold flex items-center gap-3"
                style={{ fontSize: '1.1rem', padding: '1.25rem 2.5rem' }}
              >
                <span className="material-symbols-outlined">play_circle</span>
                {t.hero.btnVideo}
              </button>
            </div>
          </div>

          {/* Right — image with glass slab visible at top-right */}
          <div
            className="md:col-span-6"
            style={{ position: 'relative', paddingTop: '2rem', paddingRight: '2rem' }}
          >
            {/* Glass slab — anchored to top-right, peeks above and right of image */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: '2rem',
                left: '2rem',
                background: 'rgba(245, 244, 243, 0.60)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
                borderRadius: '0.75rem',
                border: '1.5px solid rgba(255, 255, 255, 0.70)',
                boxShadow: '0 8px 32px rgba(0, 47, 29, 0.07), inset 0 1.5px 0 rgba(255,255,255,0.75)',
                zIndex: 0,
              }}
            />
            {/* Product image — sits bottom-left, floats above the glass */}
            <img
              src="/product-photo.jpg"
              alt="LumaSeeker P2 hands-free page magnifier for seniors"
              className="rounded-xl object-cover w-full atmospheric-shadow"
              style={{
                position: 'relative',
                zIndex: 1,
                display: 'block',
                maxHeight: '560px',
                objectPosition: 'center',
              }}
            />
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════
          BENTO — PRECISION CRAFTED
      ══════════════════════════════════════ */}
      <section id="features" className="bg-surface-container-low py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          {/* Section header */}
          <div className="mb-16">
            <span className="text-secondary font-bold tracking-widest uppercase" style={{ fontSize: '0.8rem' }}>
              {t.bento.badge}
            </span>
            <h2 className="font-bold text-primary hanging-indent mt-2" style={{ fontSize: 'clamp(2rem, 3vw, 3.5rem)' }}>
              {t.bento.title}
            </h2>
            <p className="text-on-surface-variant mt-4 hanging-indent max-w-2xl leading-relaxed" style={{ fontSize: 'clamp(1.1rem, 1.4vw, 1.3rem)' }}>
              {t.bento.desc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Large card — 5X Crystal */}
            <div className="md:col-span-2 bg-surface rounded-xl p-10 flex flex-col justify-between atmospheric-shadow overflow-hidden group">
              <div>
                <div className="w-16 h-16 bg-primary-fixed rounded-lg flex items-center justify-center mb-8">
                  <span className="material-symbols-outlined text-primary text-3xl">
                    {t.bento.items[0].icon}
                  </span>
                </div>
                <h3 className="font-bold text-primary mb-4" style={{ fontSize: 'clamp(1.6rem, 2vw, 2.25rem)' }}>{t.bento.items[0].title}</h3>
                <p className="text-on-surface-variant leading-relaxed max-w-md" style={{ fontSize: 'clamp(1rem, 1.2vw, 1.2rem)' }}>
                  {t.bento.items[0].desc}
                </p>
              </div>
              <div className="mt-8 transform group-hover:scale-[1.02] transition-transform duration-700">
                <img
                  src="/product-overview.jpg"
                  alt="5X magnifying glass with 360 degree adjustable stand"
                  className="rounded-lg object-cover w-full h-64"
                />
              </div>
            </div>

            {/* Stacked small cards */}
            <div className="flex flex-col gap-8">
              {/* Durable Alloy — dark green */}
              <div className="bg-primary-container text-on-primary rounded-xl p-8 flex flex-col justify-center flex-1">
                <span className="material-symbols-outlined text-4xl mb-4 text-on-primary-container">
                  {t.bento.items[1].icon}
                </span>
                <h4 className="font-bold mb-2" style={{ fontSize: 'clamp(1.25rem, 1.6vw, 1.75rem)' }}>{t.bento.items[1].title}</h4>
                <p className="opacity-80 leading-relaxed" style={{ fontSize: 'clamp(0.95rem, 1.1vw, 1.1rem)' }}>{t.bento.items[1].desc}</p>
              </div>
              {/* 360° — white */}
              <div className="bg-surface rounded-xl p-8 flex flex-col justify-center flex-1 atmospheric-shadow">
                <span className="material-symbols-outlined text-4xl mb-4 text-secondary">
                  {t.bento.items[2].icon}
                </span>
                <h4 className="font-bold text-primary mb-2" style={{ fontSize: 'clamp(1.25rem, 1.6vw, 1.75rem)' }}>{t.bento.items[2].title}</h4>
                <p className="text-on-surface-variant leading-relaxed" style={{ fontSize: 'clamp(0.95rem, 1.1vw, 1.1rem)' }}>{t.bento.items[2].desc}</p>
              </div>
            </div>

            {/* Full-width — Night-Sight */}
            <div className="md:col-span-3 bg-surface rounded-xl p-10 flex flex-col md:flex-row gap-8 items-center atmospheric-shadow">
              <div className="flex-1">
                <span className="material-symbols-outlined text-secondary text-4xl mb-4 block">
                  {t.bento.items[3].icon}
                </span>
                <h3 className="font-bold text-primary mb-4" style={{ fontSize: 'clamp(1.6rem, 2vw, 2.25rem)' }}>{t.bento.items[3].title}</h3>
                <p className="text-on-surface-variant leading-relaxed" style={{ fontSize: 'clamp(1rem, 1.2vw, 1.2rem)' }}>{t.bento.items[3].desc}</p>
              </div>
              <div className="flex-1 rounded-xl overflow-hidden h-48 w-full">
                <img
                  src="/product.png"
                  alt="LED reading magnifier for low vision and elderly"
                  className="w-full h-full object-contain"
                  style={{ background: '#f4f3f2' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          WHY LUMASEEKER
      ══════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-surface">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            {/* Stats grid — left on desktop */}
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-6">
                {t.why.stats.map((stat, i) => (
                  <div key={i} className="bg-surface-container rounded-xl p-6 text-center">
                    <div className="font-bold text-secondary mb-2" style={{ fontSize: 'clamp(1.75rem, 2.5vw, 2.75rem)' }}>{stat.value}</div>
                    <div className="font-medium uppercase tracking-widest text-on-surface-variant" style={{ fontSize: '0.8rem' }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Text — right on desktop */}
            <div className="order-1 lg:order-2">
              <h2 className="font-bold text-primary mb-10 leading-tight" style={{ fontSize: 'clamp(2rem, 3vw, 3.5rem)' }}>
                {t.why.title}
              </h2>
              <div className="space-y-8">
                {t.why.items.map((item, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary-fixed rounded-full flex items-center justify-center text-primary-container">
                      <span className="material-symbols-outlined font-bold">{item.icon}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-primary mb-2" style={{ fontSize: 'clamp(1.1rem, 1.4vw, 1.35rem)' }}>{item.title}</h4>
                      <p className="text-on-surface-variant leading-relaxed" style={{ fontSize: 'clamp(1rem, 1.2vw, 1.2rem)' }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════ */}
      <section id="testimonials" className="py-24 md:py-32 bg-surface-container-high">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <h2 className="font-bold text-primary" style={{ fontSize: 'clamp(2rem, 3vw, 3.5rem)' }}>{t.testimonials.title}</h2>
              <p className="text-on-surface-variant mt-4" style={{ fontSize: 'clamp(1.05rem, 1.3vw, 1.25rem)' }}>{t.testimonials.desc}</p>
            </div>
            <div className="flex gap-4 flex-shrink-0">
              <button
                onClick={() => slide('prev')}
                className="w-14 h-14 rounded-full bg-surface flex items-center justify-center cursor-pointer hover:bg-primary-fixed transition-colors atmospheric-shadow">
                <span className="material-symbols-outlined text-primary">arrow_back</span>
              </button>
              <button
                onClick={() => slide('next')}
                className="w-14 h-14 rounded-full bg-surface flex items-center justify-center cursor-pointer hover:bg-primary-fixed transition-colors atmospheric-shadow">
                <span className="material-symbols-outlined text-primary">arrow_forward</span>
              </button>
            </div>
          </div>

          <div className="overflow-hidden">
            <div
              ref={trackRef}
              style={{ display: 'flex', width: 'calc(500% / 3)' }}
            >
              {[...Array(5)].map((_, pos) => {
                const n = t.testimonials.items.length;
                const itemIdx = (testimonialIndex - 2 + pos + n * 10) % n;
                const item = t.testimonials.items[itemIdx];
                const featuredPos = slideDir === 'next' ? 3 : slideDir === 'prev' ? 1 : 2;
                const isFeatured = pos === featuredPos;
                return (
                  <div key={pos} style={{ width: 'calc(100% / 5)', padding: '0 1rem' }}>
                    <div className={`rounded-xl p-10 atmospheric-shadow h-full ${
                      isFeatured
                        ? 'bg-primary-container text-on-primary border-t-8 border-secondary'
                        : 'bg-surface'
                    }`}>
                      <div className={`flex mb-6 ${isFeatured ? 'text-primary-fixed' : 'text-secondary'}`}>
                        {[...Array(5)].map((_, j) => (
                          <span key={j} className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                        ))}
                      </div>
                      <p className={`font-medium italic leading-relaxed mb-8 ${isFeatured ? 'text-on-primary' : 'text-primary'}`}
                         style={{ fontSize: 'clamp(1.05rem, 1.3vw, 1.25rem)' }}>
                        "{item.text}"
                      </p>
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-primary ${isFeatured ? 'bg-secondary-fixed-dim' : 'bg-primary-fixed-dim'}`}>
                          {item.initials}
                        </div>
                        <div>
                          <div className={`font-bold text-base ${isFeatured ? 'text-on-primary' : 'text-primary'}`}>{item.name}</div>
                          <div className={`text-sm ${isFeatured ? 'opacity-70' : 'text-on-surface-variant'}`}>{item.meta}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          SETUP MADE SIMPLE
      ══════════════════════════════════════ */}
      <section id="setup" className="py-24 bg-surface-container-low">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center gap-16">
            {/* Image */}
            <div className="w-full md:w-1/2 flex justify-center">
              <div className="relative group">
                <div className="absolute -inset-4 bg-primary-container/5 rounded-3xl blur-2xl group-hover:bg-primary-container/10 transition-all" />
                <div className="relative z-10 w-full max-w-sm rounded-xl atmospheric-shadow overflow-hidden h-[420px] group-hover:-rotate-1 transition-transform duration-500">
                  <img
                    src="/user-manual.jpg"
                    alt="LumaSeeker User Manual"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
            {/* Text */}
            <div className="w-full md:w-1/2">
              <h2 className="font-extrabold text-primary mb-6" style={{ fontSize: 'clamp(1.75rem, 2.5vw, 3rem)' }}>{t.setup.title}</h2>
              <p className="text-on-surface-variant mb-10 leading-relaxed" style={{ fontSize: 'clamp(1rem, 1.3vw, 1.25rem)' }}>{t.setup.desc}</p>
              <div className="space-y-6">
                {t.setup.features.map((feat, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span className="material-symbols-outlined text-secondary" style={{ fontSize: '1.5rem' }}>{feat.icon}</span>
                    <span className="font-bold text-primary" style={{ fontSize: 'clamp(1rem, 1.2vw, 1.2rem)' }}>{feat.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          CONTACT
      ══════════════════════════════════════ */}
      <section id="contact" className="py-24 md:py-32 bg-surface">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <span className="text-secondary font-bold tracking-widest text-sm uppercase">
              {t.nav.support}
            </span>
            <h2 className="font-bold text-primary mt-2 hanging-indent" style={{ fontSize: 'clamp(2rem, 3vw, 3.5rem)' }}>
              {t.contact.title}
            </h2>
            <p className="text-on-surface-variant mt-4 hanging-indent max-w-2xl" style={{ fontSize: 'clamp(1.05rem, 1.3vw, 1.25rem)' }}>
              {t.contact.desc}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Info cards */}
            <div className="space-y-6">
              <div className="bg-surface-container-low rounded-xl p-8 flex items-start gap-6 atmospheric-shadow">
                <div className="w-12 h-12 bg-primary-fixed rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-primary">mail</span>
                </div>
                <div>
                  <h3 className="font-bold text-primary text-xl mb-1">{t.contact.email}</h3>
                  <p className="text-secondary font-semibold text-lg">info@lumaseeker.com</p>
                  <p className="text-on-surface-variant mt-1">{t.contact.emailReply}</p>
                </div>
              </div>
              <div className="bg-surface-container-low rounded-xl p-8 flex items-start gap-6 atmospheric-shadow">
                <div className="w-12 h-12 bg-primary-fixed rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-primary">schedule</span>
                </div>
                <div>
                  <h3 className="font-bold text-primary text-xl mb-1">{t.contact.hours}</h3>
                  <p className="text-on-surface-variant text-lg">{t.contact.weekdays}</p>
                  <p className="text-on-surface-variant mt-1">8:30 AM – 5:00 PM EST</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-surface-container-low rounded-xl p-10 atmospheric-shadow">
              <h3 className="text-2xl font-bold text-primary mb-8">{t.contact.form.title}</h3>
              <form className="space-y-6" onSubmit={handleFormSubmit}>
                <div>
                  <label className="block text-sm font-semibold text-primary mb-2 uppercase tracking-wide">
                    {t.contact.form.name}
                  </label>
                  <input
                    type="text"
                    name="from_name"
                    className="input-field w-full px-4 py-3"
                    placeholder={t.contact.form.namePlaceholder}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-primary mb-2 uppercase tracking-wide">
                    {t.contact.form.emailLabel}
                  </label>
                  <input
                    type="email"
                    name="from_email"
                    className="input-field w-full px-4 py-3"
                    placeholder={t.contact.form.emailPlaceholder}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-primary mb-2 uppercase tracking-wide">
                    {t.contact.form.message}
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    className="input-field w-full px-4 py-3 resize-none"
                    placeholder={t.contact.form.messagePlaceholder}
                    required
                  />
                </div>
                <button type="submit" className="btn-primary w-full py-4 font-bold text-lg">
                  {t.contact.form.submit}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════ */}
      <section id="cta" className="py-32" style={{ background: '#002f1d' }}>
        <div className="max-w-screen-xl mx-auto px-6 text-center">
          <h2 className="font-bold text-primary-fixed mb-8 leading-tight" style={{ fontSize: 'clamp(2.2rem, 5vw, 5.5rem)' }}>
            {t.cta.title}
          </h2>
          <p className="max-w-3xl mx-auto mb-16 leading-relaxed" style={{ fontSize: 'clamp(1.1rem, 1.5vw, 1.5rem)', color: 'rgba(186,238,209,0.8)' }}>
            {t.cta.desc}
          </p>
          <div className="flex flex-col md:flex-row gap-6 justify-center">
            <button
              className="rounded-lg font-bold hover:opacity-90 transition-all atmospheric-shadow uppercase tracking-tight"
              style={{ background: '#795900', color: '#ffffff', fontSize: 'clamp(1rem, 1.3vw, 1.25rem)', padding: 'clamp(1rem, 1.5vh, 1.5rem) clamp(2rem, 3vw, 3rem)' }}
              onClick={() => setIsPresaleOpen(true)}
            >
              {t.cta.btnBuy}
            </button>
            <button
              className="bg-transparent border-2 rounded-lg font-bold hover:bg-primary-container transition-all"
              style={{ borderColor: '#baeed1', color: '#baeed1', fontSize: 'clamp(1rem, 1.3vw, 1.25rem)', padding: 'clamp(1rem, 1.5vh, 1.5rem) clamp(2rem, 3vw, 3rem)' }}
            >
              {t.cta.btnContact}
            </button>
          </div>
          <div className="mt-14 flex flex-wrap justify-center gap-8 font-medium" style={{ color: 'rgba(186,238,209,0.6)' }}>
            {t.cta.badges.map((badge, i) => (
              <span key={i} className="flex items-center gap-2">
                <span className="material-symbols-outlined text-xl">{t.cta.badgeIcons[i]}</span>
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FOOTER
      ══════════════════════════════════════ */}
      <footer className="w-full bg-surface-container-low">
        <div className="flex flex-col md:flex-row justify-between items-start px-6 md:px-12 py-16 gap-12 max-w-screen-2xl mx-auto">
          {/* Brand */}
          <div className="flex flex-col gap-5 max-w-xs">
            <div className="flex items-center gap-3">
              <img src="/favicon-32x32.png" alt="LumaSeeker" className="h-10 w-auto" />
              <span className="text-xl font-bold text-primary">LumaSeeker</span>
            </div>
            <p className="text-on-surface-variant leading-relaxed">{t.footer.desc}</p>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 text-base flex-1">
            {t.footer.cols.map((col, i) => (
              <div key={i} className="flex flex-col gap-4">
                <h5 className="font-bold text-primary">{col.heading}</h5>
                {col.links.map((link, j) => (
                  <button key={j} className="text-left text-primary-container opacity-80 hover:opacity-100 hover:text-primary transition-opacity">
                    {link}
                  </button>
                ))}
              </div>
            ))}

            {/* Newsletter */}
            <div className="flex flex-col gap-5">
              <h5 className="font-bold text-primary">{t.footer.stayInformed}</h5>
              <div className="flex flex-col gap-2">
                <input
                  type="email"
                  className="input-field w-full px-3 py-2 text-sm"
                  placeholder={t.footer.emailPlaceholder}
                />
                <button className="px-4 py-2 rounded-lg font-bold text-sm text-white w-full" style={{ background: '#795900' }}>
                  {t.footer.joinBtn}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-outline-variant/10 px-6 md:px-12 py-7 max-w-screen-2xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <span className="text-on-surface-variant/60 text-sm uppercase tracking-wide">
            {t.footer.rights}
          </span>
          <div className="flex gap-8 text-sm font-medium text-on-surface-variant/80">
            <button className="hover:text-primary transition-colors">{t.footer.a11y}</button>
            <button className="hover:text-primary transition-colors">{t.footer.typeface}</button>
          </div>
        </div>
      </footer>

      {/* ══════════════════════════════════════
          VIDEO MODAL
      ══════════════════════════════════════ */}
      {isPresaleOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn"
          onClick={() => setIsPresaleOpen(false)}
        >
          <div
            className="relative w-full max-w-md bg-surface rounded-2xl shadow-2xl p-10 text-center animate-scaleIn"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setIsPresaleOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-container transition-all"
            >
              <span className="material-symbols-outlined text-on-surface-variant">close</span>
            </button>
            <div className="text-4xl mb-4">📦</div>
            <h2 className="font-bold text-on-surface mb-3" style={{ fontSize: '1.5rem' }}>
              {t.presale.title}
            </h2>
            <p className="text-on-surface-variant leading-relaxed mb-5">
              {t.presale.body}
            </p>
            <a
              href={`mailto:${t.presale.email}`}
              className="inline-block font-bold text-secondary hover:underline text-lg mb-8"
            >
              {t.presale.email}
            </a>
            <br />
            <button
              onClick={() => setIsPresaleOpen(false)}
              className="btn-primary px-8 py-3 font-bold mt-2"
            >
              {t.presale.close}
            </button>
          </div>
        </div>
      )}

      {isVideoOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn"
          onClick={closeVideo}
        >
          <div
            className="relative w-full max-w-4xl bg-white/10 backdrop-blur-md rounded-xl shadow-2xl overflow-hidden animate-scaleIn"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={closeVideo}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
            >
              <span className="material-symbols-outlined text-white">close</span>
            </button>
            <div className="relative aspect-video">
              <video
                ref={videoRef}
                className="w-full h-full"
                controls
                autoPlay
                playsInline
              >
                <source src="/videos/product-demo.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
